/**
 * Buttons
 */
import * as React from 'react';
import { colors, fontSizes, fontFamily } from './styles';
import { style, extend } from 'typestyle';
import { LoaderStyles, LoaderWhite, Loader } from './loader';

/**
 * Built it for https://app.frontify.com/d/zYF6HCW0aIp5/ui-library#/atoms/buttons
 */
export namespace ButtonStyles {
  const baseStyle = {
    fontFamily: fontFamily.auspost,
    cursor: 'pointer',
    height: 'auto',
    padding: "12px 30px 11px",
    border: `1px solid ${colors.red}`,
    borderRadius: '3px',
    color: colors.red,
    backgroundColor: colors.white,
    fontSize: fontSizes.buttonText,
    textDecoration: "none",
    lineHeight: "1em",
    outline: 'none',
    transition: 'color .2s, background-color .2s',
    '&:focus': {
      outline: 'thin dotted',
      outlineColor: colors.red
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: colors.warmGrey4,
      borderColor: colors.warmGrey4,
      cursor: 'not-allowed'
    }
  };

  export const primaryClassName = style(baseStyle, {
    position: 'relative',

    display: 'inline-block',
    color: colors.white,
    backgroundColor: colors.red,
    $nest: {
      '&:hover': {
        backgroundColor: colors.hoverRed,
      },
      '&:active': {
        backgroundColor: colors.activeRed,
      },
    }
  });

  /**
  * This is like primary however it removes the `disabled` styling
  * Reason:
  * - We want the loading `input` to be `disabled`
  *  otherwise the form will still get `submit`
  *  Hence need to remove any *special* loading styling
  */
  export const loadingClassName = style({
    position: 'relative',

    display: 'inline-block',
    cursor: 'pointer',
    height: 'auto',
    padding: "12px 30px 11px",
    border: `1px solid ${colors.red}`,
    borderRadius: '3px',
    fontSize: fontSizes.buttonText,
    textDecoration: "none",
    lineHeight: "1em",
    outline: 'none',
    color: colors.white,
    backgroundColor: colors.red,
    $nest: {
      '&:focus': {
        outline: 'thin dotted',
        outlineColor: colors.red
      },
      '&:hover': {
        backgroundColor: colors.hoverRed,
      },
      '&:active': {
        backgroundColor: colors.activeRed,
      },
      '&:disabled': {
        cursor: 'not-allowed'
      }
    }
  });

  export const secondaryClassName = style(baseStyle, {
    display: 'inline-block',
    color: colors.red,
    backgroundColor: colors.transparent,
    $nest: {
      '&:hover': {
        color: colors.white,
        backgroundColor: colors.hoverRed,
      },
      '&:active': {
        color: colors.white,
        backgroundColor: colors.activeRed,
      },
    }
  });

  export const tertiaryClassName = style(baseStyle, {
    display: 'inline-block',
    border: `1px solid ${colors.warmGrey5}`,
    color: colors.warmGrey5,
    backgroundColor: colors.transparent,
    $nest: {
      '&:focus': {
        outline: 'thin dotted',
        outlineColor: colors.warmGrey5
      },
      '&:hover': {
        border: `1px solid ${colors.red}`,
        color: colors.red,
        backgroundColor: colors.transparent,
        outlineColor: colors.red
      },
      '&:active': {
        border: `1px solid ${colors.activeRed}`,
        color: colors.activeRed,
        backgroundColor: colors.transparent,
      }
    }
  });
}

interface ButtonProps {
  text: string;
  id?: string;
  onClick?: () => any;
  disabled?: boolean;
  width?: string;
}

interface PrimaryButtonProps extends ButtonProps {
  loading?: boolean;
}

export const PrimaryButton = ({text, id, onClick, disabled, loading, width = "auto"}: PrimaryButtonProps) => {
  return (!loading
    ? <button id={id} className={ButtonStyles.primaryClassName} type="button" onClick={onClick} disabled={disabled} style={{ width }}>
      <span>{text}</span>
    </button>
    : <button id={id} type="button" className={ButtonStyles.loadingClassName} disabled={true}>
      <span style={{ color: 'transparent' }}>{text}</span>
      <div style={{ position: 'absolute', left: '43%', top: '25%' }}><LoaderWhite /></div>
    </button>
  );
}

export const SecondaryButton = ({text, id, onClick, disabled, width = "auto"}: ButtonProps) => {
  return (
    <button id={id} className={ButtonStyles.secondaryClassName} type="button" onClick={onClick} disabled={disabled} style={{ width }}>
      <span>{text}</span>
    </button>
  );
}

export const TertiaryButton = ({text, id, onClick, disabled, width = "auto"}: ButtonProps) => {
  return (
    <button id={id} className={ButtonStyles.tertiaryClassName} type="button" onClick={onClick} disabled={disabled} style={{ width }}>
      <span>{text}</span>
    </button>
  );
}
